/**
 * Money on its way back.
 *
 * ---------------------------------------------------------------------------
 * INITIATED AND SETTLED ARE TWO DIFFERENT THINGS
 * ---------------------------------------------------------------------------
 * The gateway confirms a refund the moment it is raised. The bank posts it to
 * the customer's account five to seven working days later. Collapsing those
 * into one state - "Refunded" - is what generates a support ticket for every
 * single refund, because the app said the money was back and the statement
 * says it is not.
 *
 * So the app carries both, and always shows which one it is in, with a date
 * for the one that has not happened yet. That is the entire reason this file
 * exists rather than a boolean on the booking.
 */

import { Booking } from './bookings';

export type RefundStatus = 'initiated' | 'settled';

export type Refund = {
  /** What support quotes back - see `refundIdFor` in `payment.ts`. */
  id: string;
  status: RefundStatus;
  /** What actually returns to the customer, after any fee. */
  amount: number;
  /** Kept out of the refund and stated separately. Zero unless charged. */
  fee: number;
  /** Where it goes back to - always the way it was paid. */
  method: string;
  initiatedOn: string;
  /** The bank's window, shown while `initiated`. */
  expectedBy: string;
  /** Only once the webhook has landed. */
  settledOn?: string;
};

/**
 * The refunds on file, keyed by booking.
 *
 * Two seeded, deliberately one of each status: the pair is the only way to see
 * both halves of the timeline without a backend to advance one.
 */
export const seededRefunds: Record<string, Refund> = {
  /*
   * Cancelled by the customer inside the late window: the fee was kept, and
   * the money is still with the bank. The pair of figures - 1,299 paid, 1,250
   * back, 49 kept - is the arithmetic the timeline has to show rather than
   * leave somebody to reverse-engineer.
   */
  'bk-1019': {
    id: 'rfnd_HOM12602',
    status: 'initiated',
    amount: 1250,
    fee: 49,
    method: 'HDFC Credit Card •••• 4821',
    initiatedOn: '21 May 2025',
    expectedBy: '28 May 2025',
  },
  /* Cancelled by us, refunded in full, and long enough ago to have landed. */
  'bk-1007': {
    id: 'rfnd_HOM12488',
    status: 'settled',
    amount: 899,
    fee: 0,
    method: 'UPI • rohan@okhdfcbank',
    initiatedOn: '2 May 2025',
    expectedBy: '9 May 2025',
    settledOn: '7 May 2025',
  },
};

/** The refund for a booking, if one was ever raised. */
export const refundFor = (booking: Booking): Refund | undefined =>
  seededRefunds[booking.id];

/**
 * Raises a refund against a booking, for real - as real as anything here
 * gets. See the file note: `seededRefunds` standing in for a backend is the
 * whole reason this file exists, so writing a fresh cancellation into it is
 * not a special case, it is the file doing its one job.
 *
 * `seededRefunds` is not React state - nothing rerenders because this ran -
 * which is exactly why it can be mutated directly rather than rebuilt: the
 * caller already has its own reason to rerender (the booking's own status
 * just changed), and `refundFor` reads this object fresh on every call.
 */
export const recordRefund = (bookingId: string, refund: Refund): void => {
  seededRefunds[bookingId] = refund;
};

/**
 * What the app says while the money is in transit.
 *
 * A sentence rather than a status word, because "Initiated" answers a question
 * nobody asked. What is actually wanted is "when will it be in my account",
 * and that is the only thing this returns.
 */
export const refundLine = (refund: Refund): string =>
  refund.status === 'settled'
    ? `Refunded on ${refund.settledOn}`
    : `Expected in your account by ${refund.expectedBy}`;
